import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterClass,
  // Spell,
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DandDAPI from "@dndUtilities/system_api";

import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import CharacterManageSpellsGroup from "@dndComponents/character/spells_and_abilities/CharacterManageSpellsGroup";
import FloatMenu from "@components/input/menus/FloatMenu";
import StringBox from "@components/input/strings/StringBox";
import CheckBox from "@components/input/CheckBox";
// import ButtonBox from '../../../../../components/input/buttons/ButtonBox';
// import CharacterManageInfusions from './CharacterManageInfusions';

interface Props {
  character: Character;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterManageSpells({
  character,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloading_classes, setReloadingClasses] = useState(false);
  const [selected_char_class, setSelectedCharClass] = useState<CharacterClass | undefined>(undefined);
  const [subgroup, setSubgroup] = useState<"Cantrips" | "Add Spells to Book" | "Prepare Spells" | "Customize" | "Spells" | "">("Prepare Spells");
  const [extra_spells_mode, setExtraSpellsMode] = useState(false);
  const [ping, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const spellsChanged = () => {
    setReloadingClasses(true);
  }
    
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      const new_selected = character.classes.find(o => o.spellcasting_ability !== "None");
      setSelectedCharClass(new_selected);
    }
  }, [fetched, character]);

  useEffect(() => {
    character.subscription_holder.subscribe("Spells", mounted, spellsChanged);
  }, [character, spellsChanged]);

  useEffect(() => {
    if (reloading_classes) {
      setReloadingClasses(false);
    }
  }, [reloading_classes, setReloadingClasses]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderMagicalClass = (use_char_class: CharacterClass) => {
    if (use_char_class.source) {
      // debugger;
      let display = use_char_class.source.name;
      const extra_mapping: { [key: string]: string } = {};
      if (use_char_class.cantrips_max > 0) {
        if (use_char_class.cantrips.length !== use_char_class.cantrips_max) {
          display += ` Cantrips (${use_char_class.cantrips.length} / ${use_char_class.cantrips_max})`;
          extra_mapping["Cantrips"] = `(${use_char_class.cantrips.length} / ${use_char_class.cantrips_max})`;
        } else {
          display += ` Cantrips (${use_char_class.cantrips_max})`;
          extra_mapping["Cantrips"] = `(${use_char_class.cantrips_max})`;
        }
      }
      if (use_char_class.spells_prepared_max > 0) {
        if (use_char_class.leveled_spells.length !== use_char_class.spells_prepared_max) {
          display += ` Spells (${use_char_class.leveled_spells.length} / ${use_char_class.spells_prepared_max})`;
          extra_mapping["Prepare Spells"] = `(${use_char_class.leveled_spells.length} / ${use_char_class.spells_prepared_max})`;
        } else {
          display += ` Spells (${use_char_class.spells_prepared_max})`;
          extra_mapping["Prepare Spells"] = `(${use_char_class.spells_prepared_max})`;
        }
      }

      const colors = char_util.getColors({ char: character, user: api.user, for_id: use_char_class.source_id });
      return (
        <Grid
          key={use_char_class.true_id}
          size={{
            xs: 12
          }}>
          <ToggleButtonBox 
            name={ display }
            value={ selected_char_class !== undefined && use_char_class.source_id === selected_char_class.source_id }
            colors={ colors }
            onToggle={() => {
              if (selected_char_class && selected_char_class.source_id === use_char_class.source_id) {
                setSelectedCharClass(undefined);
              } else {
                setSelectedCharClass(use_char_class);
              }
              if (use_char_class.spellbook) {
                setSubgroup("Add Spells to Book");
              } else {
                setSubgroup("");
              }
              setReloading(true);
            }}
          />
        </Grid>
      );
    }
    return null;
  }

  const renderSubgroups = () => {
    const cc = selected_char_class;
    if (cc) {
      const extra_mapping: { [key: string]: string } = {};
      if (cc.cantrips_max > 0) {
        if (cc.cantrips.length !== cc.cantrips_max) {
          extra_mapping["Cantrips"] = `(${cc.cantrips.length} / ${cc.cantrips_max})`;
        } else {
          extra_mapping["Cantrips"] = `(${cc.cantrips_max})`;
        }
      }
      if (cc.spell_infusions) {
        // Infusions have counts specific to level, which makes it too complicated to display here.
        // I will make it just display whether or not there are some that need placing.
      } else {
        if (cc.spells_prepared_max > 0) {
          if (cc.leveled_spells.length !== cc.spells_prepared_max) {
            extra_mapping["Prepare Spells"] = `(${cc.leveled_spells.length} / ${cc.spells_prepared_max})`;
          } else {
            extra_mapping["Prepare Spells"] = `(${cc.spells_prepared_max})`;
          }
        }
      }
      if (cc.spellbook && cc.spellbook.spellbook) {
        const char_spellbook = cc.spellbook;
        const options = ["Cantrips","Add Spells to Book"];
        if (cc.spell_infusions) {
          // options.push("Infusions");
        } else {
          options.push("Prepare Spells");
        }
        options.push("Customize");
        if (char_spellbook.total_unused_free_spells > 0) {
          extra_mapping["Add Spells to Book"] = `(${char_spellbook.total_unused_free_spells} Free)`;
        }
        return (
          <Grid
            key={`${cc.position}_subgroups`}
            size={{
              xs: 12
            }}>
            <FloatMenu
              options={options}
              selected={ subgroup === "" ? "Add Spells to Book" : subgroup }
              extra_mapping={extra_mapping}
              onSelect={(changed: string) => {
                setSubgroup(changed as "Cantrips" | "Add Spells to Book" | "Prepare Spells" | "Customize" | "Spells" | "");
              }}
            />
          </Grid>
        );
      } else {
        return (
          <Grid
            key={`${cc.position}_subgroups`}
            size={{
              xs: 12
            }}>
            <FloatMenu
              options={["Spells","Customize"]}
              selected={ subgroup === "" ? "Spells" : subgroup }
              extra_mapping={extra_mapping}
              onSelect={(changed: string) => {
                setSubgroup(changed as "Cantrips" | "Add Spells to Book" | "Prepare Spells" | "Customize" | "Spells" | "");
              }}
            />
          </Grid>
        );
      }
    }
    return null;
  }

  const renderCustomizations = () => {
    // console.log(selected_char_class);
    if (selected_char_class) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Extra Cantrips"
              value={selected_char_class.spellcasting.extra_cantrips}
              type="number"
              onBlur={(changed: string) => {
                const num = Math.max(+changed,0);
                if (selected_char_class) {
                  selected_char_class.spellcasting.extra_cantrips = num;
                }
                setPing(!ping);
              }}
            />
          </Grid>
          { selected_char_class.spellcasting.spellbook &&
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Extra Free Spells in Book"
                value={selected_char_class.spellcasting.spellbook.extra_free_spells}
                type="number"
                onBlur={(changed: string) => {
                  const num = Math.max(+changed,0);
                  if (selected_char_class && selected_char_class.spellcasting.spellbook) {
                    selected_char_class.spellcasting.spellbook.extra_free_spells = num;
                  }
                  setPing(!ping);
                }}
              />
            </Grid>
          }
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Extra Spells Prepared"
              value={selected_char_class.spellcasting.extra_spells_prepared}
              type="number"
              onBlur={(changed: string) => {
                const num = Math.max(+changed,0);
                if (selected_char_class) {
                  selected_char_class.spellcasting.extra_spells_prepared = num;
                }
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <CheckBox
              name="Add Extras in Main Tools"
              value={extra_spells_mode}
              onChange={setExtraSpellsMode}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }

  return (
    <Grid container spacing={0} direction="row">
      { !reloading_classes && character.classes.filter(o => o.spellcasting_ability !== "None").map(cc => {
        return renderMagicalClass(cc);
      })}
      { renderSubgroups() }
      <Grid size={12}>
        { selected_char_class && !reloading && 
          <>
            { subgroup !== "Customize" &&
              <CharacterManageSpellsGroup 
                character={character} 
                char_class={selected_char_class} 
                subgroup={subgroup} 
                extra_spells_mode={extra_spells_mode}
              />
            }
          </>
        }
        { subgroup === "Customize" &&
          renderCustomizations()
        }
      </Grid>
    </Grid>
  );
}
